import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, ArrowLeft, RotateCcw, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";

interface ArchivedBid {
  _id: Id<"bids">;
  title: string;
  client: {
    company: string;
    contactName: string;
    email: string;
    phone: string;
  };
  status: string;
  value: string;
  lastModified: string;
  createdAt: number;
}

export default function ArchivedBidList() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Fetch archived bids from Convex
  const archivedBids = useQuery(api.bids.getArchivedBids) ?? [];
  const unarchiveBid = useMutation(api.bids.unarchive);

  const filteredBids = archivedBids.filter(
    (bid: any) =>
      bid.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bid.client.company.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUnarchive = async (bidId: Id<"bids">, bidTitle: string) => {
    try {
      await unarchiveBid({ id: bidId });
      toast.success(`${bidTitle} has been unarchived successfully`);
    } catch (error) {
      console.error("Failed to unarchive bid:", error);
      toast.error("Failed to unarchive bid");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-700";
      case "Submitted":
        return "bg-blue-100 text-blue-700";
      case "In Review":
        return "bg-yellow-100 text-yellow-700";
      case "Rejected":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/bids")} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Archived Bids</h1>
            <p className="mt-1 text-gray-500">View and manage archived bid proposals</p>
          </div>
        </div>
      </div>

      <Card className="p-6">
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search archived bids..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" /> Filter
          </Button>
        </div>

        {filteredBids.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-gray-400">
              <FileText className="mx-auto h-12 w-12" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">No archived bids found</h3>
            <p className="text-gray-500">
              {searchQuery
                ? "Try adjusting your search criteria"
                : "You haven't archived any bids yet"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Contract Value</TableHead>
                <TableHead>Last Modified</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBids.map((bid: any) => (
                <TableRow key={bid._id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      {bid.title}
                    </div>
                  </TableCell>
                  <TableCell>{bid.client.company}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={getStatusColor(bid.status)}>
                      {bid.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{bid.value}</TableCell>
                  <TableCell>{new Date(bid.lastModified).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => handleUnarchive(bid._id, bid.title)}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Unarchive
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
